/**
 * Trailing-slash preference for the feed's page URLs (#1848).
 *
 * `additional_info.reverseProxy.trailingSlash` is `"keep"` or `"strip"`;
 * absent reads as `strip`, the slash-less form every link built before the
 * setting existed. The worker canonicalizes (and 301s) onto the same form, so
 * a link built here lands on the page in one hop instead of paying a redirect
 * per click. The renderer imports THIS copy; the dashboard and the worker
 * template carry their own (they cannot import this package); the three must
 * agree.
 */
export type TrailingSlashMode = 'keep' | 'strip'

type FeedLike = { additional_info?: unknown } | null | undefined

/** Tolerant read: anything but the literal "keep" is strip. */
export function trailingSlashModeOf(feed: FeedLike): TrailingSlashMode {
  const info = feed?.additional_info as { reverseProxy?: { trailingSlash?: unknown } } | undefined
  return info?.reverseProxy?.trailingSlash === 'keep' ? 'keep' : 'strip'
}

/**
 * Apply the preference to a path. Under `strip` the path comes back untouched
 * (byte for byte — every link built before the setting existed stays the
 * same). Under `keep` every trailing slash is stripped and exactly one is
 * added, so the result is a fixed point — but only for a page-like path:
 * never `/`, never a last segment carrying a dot (`sitemap-properties.xml`,
 * `x.md`; file-like URLs stay slash-less). A query string or fragment is kept
 * after the slash.
 */
export function withTrailingSlash(path: string, mode: TrailingSlashMode): string {
  const raw = String(path ?? '')
  if (mode !== 'keep') return raw
  const cut = raw.search(/[?#]/)
  const suffix = cut === -1 ? '' : raw.slice(cut)
  const base = (cut === -1 ? raw : raw.slice(0, cut)).replace(/\/+$/, '') || '/'
  if (base === '/') return base + suffix
  const last = base.slice(base.lastIndexOf('/') + 1)
  if (last.includes('.')) return base + suffix
  return base + '/' + suffix
}
